import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { WriteCheckInput } from '@accounting/shared';
import { useCheck, useNextCheckNumber, useUpdateCheck, useWriteCheck } from '../../hooks/useChecks';
import { useUncashedChecks } from '../../hooks/usePayroll';
import { useCheckSetups } from '../../hooks/useCheckPrinting';
import { useAccounts } from '../../hooks/useAccounts';
import { useContacts } from '../../hooks/useContacts';
import { useCompanies } from '../../hooks/useCompanies';
import { AccountPicker } from '../accounts/AccountPicker';
import { ContactPicker } from '../contacts/ContactPicker';
import { ApiError } from '../../lib/api';
import { withDuplicateConfirm } from '../../lib/duplicateConfirm';
import { amountToWords } from '@accounting/shared';
import { companyAddressLines, formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

interface AllocRow {
  accountId: string;
  amount: string;
  memo: string;
  /**
   * Set when this line pays a liability already on the books (an accrued
   * paycheque) rather than a fresh expense. The account is then fixed to the
   * accrual's account and the label says which entry it settles.
   */
  settlesJournalLineId?: string;
  settlesLabel?: string;
}

interface Props {
  companyId: string;
  /** Seed from this check but issue a new one (number/date reset). */
  copyFromId?: string;
  /** Edit this check in place. */
  editId?: string;
  /** Start from an accrued paycheque: the journal line whose net pay this check pays. */
  payLineId?: string;
}

export function CheckEditor({ companyId, copyFromId, editId, payLineId }: Props) {
  const navigate = useNavigate();
  const isEdit = !!editId;
  const sourceId = editId ?? copyFromId;

  const { data: companies = [] } = useCompanies();
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: payees = [] } = useContacts(companyId, {});
  const { data: source } = useCheck(companyId, sourceId ?? '');
  // Paycheques accrued by payroll and not yet paid — offered as things this
  // check can pay instead of an expense, so net pay is never booked twice.
  const { data: uncashed = [] } = useUncashedChecks(companyId);
  const write = useWriteCheck(companyId);
  const update = useUpdateCheck(companyId);
  const mutation = isEdit ? update : write;

  const [bankAccountId, setBankAccountId] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState(todayIso());
  const [payeeContactId, setPayeeContactId] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<AllocRow[]>([{ accountId: '', amount: '', memo: '' }]);

  // Only accounts explicitly flagged "Writes checks" on the Chart of Accounts.
  // Every asset account is technically payable-from, and offering all of them
  // put the whole chart in this dropdown — which is how a payment ends up drawn
  // on the wrong account.
  const bankAccounts = accounts.filter(
    (a) => a.isActive && a.allowDirectPosting && a.allowCheckWriting,
  );
  // A check spends money — revenue accounts must never be selectable, or a payment
  // would be miscoded as income and over-report revenue. Keep any account already on
  // a line visible (e.g. a revenue account picked by mistake) so it can be corrected.
  const usedAccountIds = new Set(lines.map((l) => l.accountId).filter(Boolean));
  const isAllocSelectable = (a: typeof accounts[number]) =>
    (a.isActive &&
      a.allowDirectPosting &&
      a.accountType !== 'revenue' &&
      a.accountType !== 'other_income') ||
    usedAccountIds.has(a.id);

  // Default the bank account only for a fresh new check.
  useEffect(() => {
    if (!isEdit && !sourceId && !bankAccountId && bankAccounts.length > 0) {
      setBankAccountId(bankAccounts[0]!.id);
    }
  }, [bankAccounts, bankAccountId, isEdit, sourceId]);

  // Suggest the next number for the chosen account (create only).
  const { data: suggested } = useNextCheckNumber(companyId, bankAccountId);
  const [numberTouched, setNumberTouched] = useState(false);
  useEffect(() => {
    if (!isEdit && suggested && !numberTouched) setCheckNumber(suggested);
  }, [suggested, numberTouched, isEdit]);

  // Can this account feed the batch printer? If so, queueing is the default and
  // the number is left blank — the print run assigns it, so nothing is burned if
  // this check is edited or cancelled before the paper exists.
  const { data: setups = [] } = useCheckSetups();
  const printSetup = setups.find(
    (s) => s.companyId === companyId && s.accountId === bankAccountId && s.checkWritingEnabled,
  );
  const canQueue = !!printSetup;
  const [queueMode, setQueueMode] = useState(true);
  // An account with no print setup can only ever record a hand-written check.
  const isQueued = canQueue && queueMode;

  // Seed from the source check (copy or edit).
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!sourceId || prefilledRef.current || !source) return;
    prefilledRef.current = true;
    setBankAccountId(source.bankAccountId);
    setPayeeContactId(source.payeeContactId ?? '');
    setMemo(source.memo ?? '');
    setLines(
      source.lines.map((l) => ({
        accountId: l.accountId,
        amount: String(parseFloat(l.amount)),
        memo: l.memo ?? '',
        settlesJournalLineId: l.settlesJournalLineId ?? undefined,
        settlesLabel: l.settlesEntryNumber ? `Pays ${l.settlesEntryNumber}` : undefined,
      })),
    );
    if (isEdit) {
      setCheckNumber(source.checkNumber ?? '');
      setCheckDate(source.checkDate);
      setNumberTouched(true);
      // A queued check stays queued through an edit; an issued one keeps its number.
      setQueueMode(source.status === 'to_print');
    }
  }, [sourceId, source, isEdit]);

  // Turn an open paycheque into an allocation line: debit the holding
  // liability for what is still owed, linked back to the accrual.
  const payLineFor = (c: (typeof uncashed)[number]): AllocRow => ({
    accountId: c.accountId,
    amount: String(parseFloat(c.outstanding)),
    memo: `Net pay ${formatAccountingDate(c.payDate)}`,
    settlesJournalLineId: c.journalLineId,
    settlesLabel: `Pays ${c.entryNumber}${c.employeeName ? ` — ${c.employeeName}` : ''}`,
  });

  // Arrived from the uncashed-paycheques page: seed the whole check from it.
  const payLineRef = useRef(false);
  useEffect(() => {
    if (!payLineId || payLineRef.current || isEdit) return;
    const hit = uncashed.find((c) => c.journalLineId === payLineId);
    if (!hit) return;
    payLineRef.current = true;
    setLines([payLineFor(hit)]);
    if (hit.employeeContactId) setPayeeContactId(hit.employeeContactId);
    if (!memo) setMemo(`Payroll ${formatAccountingDate(hit.payDate)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payLineId, uncashed, isEdit]);

  const addPaycheque = (journalLineId: string) => {
    const hit = uncashed.find((c) => c.journalLineId === journalLineId);
    if (!hit) return;
    setLines((prev) => {
      if (prev.some((l) => l.settlesJournalLineId === journalLineId)) return prev;
      // Replace the single empty starter row rather than leaving it dangling.
      const kept = prev.filter((l) => l.accountId || l.amount || l.memo);
      return [...kept, payLineFor(hit)];
    });
    if (hit.employeeContactId && !payeeContactId) setPayeeContactId(hit.employeeContactId);
  };
  const payableCheques = uncashed.filter(
    (c) => !lines.some((l) => l.settlesJournalLineId === c.journalLineId),
  );

  const company = companies.find((c) => c.id === companyId);
  const companyName = company?.name ?? 'Company';
  const addressLines = companyAddressLines(company);
  const payee = payees.find((p) => p.id === payeeContactId);
  const payeeAddrLines = payee
    ? [
        payee.addressLine1,
        payee.addressLine2,
        [payee.city, [payee.region, payee.postalCode].filter(Boolean).join(' ').trim()]
          .filter(Boolean)
          .join(', '),
      ].filter((l): l is string => !!l)
    : [];

  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const totalStr = total.toFixed(2);

  const setLine = (i: number, patch: Partial<AllocRow>) =>
    setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addLine = () => setLines((prev) => [...prev, { accountId: '', amount: '', memo: '' }]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  // Block editing of a check that is neither outstanding nor still queued (the
  // backend also enforces this).
  if (isEdit && source && source.status !== 'outstanding' && source.status !== 'to_print') {
    return (
      <div>
        <Link to="/checks/$id" params={{ id: editId! }} style={{ fontSize: 13 }}>
          ← Check {source.checkNumber ? `#${source.checkNumber}` : 'to print'}
        </Link>
        <div className="card" style={{ padding: 20, marginTop: 8 }}>
          <p style={{ marginTop: 0 }}>
            This check can’t be edited because it is {source.status}
            {source.status === 'cleared'
              ? ' — un-clear it first if you need to change it.'
              : source.status === 'printing'
                ? ' — confirm or discard the print run first.'
                : '.'}
          </p>
        </div>
      </div>
    );
  }

  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Could not save check'
      : mutation.error?.message;

  const valid =
    bankAccountId &&
    (isQueued || checkNumber.trim()) &&
    payeeContactId &&
    total > 0 &&
    lines.some((l) => l.accountId && parseFloat(l.amount) > 0);

  // "Save & New": write the check, then reset for the next one instead of navigating.
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  const resetForNext = () => {
    setPayeeContactId('');
    setMemo('');
    setLines([{ accountId: '', amount: '', memo: '' }]);
    // Clear the number and let the (now-invalidated) next-number query suggest the next one.
    setNumberTouched(false);
    setCheckNumber('');
    setFlash('Check saved — ready for the next one.');
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
    // The bank account and date are kept so several checks are quick to write in a row.
  };

  const submit = (andNew = false) => {
    setFlash(null);
    const input: WriteCheckInput = {
      bankAccountId,
      // Omitted entirely when queued — that is what tells the API to hold the
      // check for the batch printer instead of issuing it now.
      checkNumber: isQueued ? undefined : checkNumber.trim(),
      checkDate,
      payeeContactId,
      memo: memo.trim() || undefined,
      lines: lines
        .filter((l) => l.accountId && parseFloat(l.amount) > 0)
        .map((l) => ({
          accountId: l.accountId,
          amount: String(parseFloat(l.amount)),
          memo: l.memo.trim() || undefined,
          settlesJournalLineId: l.settlesJournalLineId ?? undefined,
        })),
    };
    if (isEdit && editId) {
      update.mutate({ id: editId, input }, { onSuccess: (c) => void navigate({ to: '/checks/$id', params: { id: c.id } }) });
      return;
    }
    // If the server spots a check just like this one, ask before writing a
    // second payment to the same payee.
    void withDuplicateConfirm(input, (i) => write.mutateAsync(i)).then((c) => {
      if (!c) return;
      if (andNew) resetForNext();
      else void navigate({ to: '/checks/$id', params: { id: c.id } });
    });
  };

  const heading = isEdit
    ? source?.checkNumber
      ? `Edit check #${source.checkNumber}`
      : 'Edit queued check'
    : copyFromId
      ? 'Copy check'
      : 'Write a check';

  return (
    <div>
      <Link to="/checks" style={{ fontSize: 13 }}>← Check register</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>{heading}</h1>

      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <label className="check-label" htmlFor="bank">Pay from account</label>
        <div style={{ maxWidth: 420 }}>
          <AccountPicker
            id="bank"
            accounts={accounts}
            value={bankAccountId}
            onChange={setBankAccountId}
            predicate={(a) => a.isActive && a.allowDirectPosting && a.allowCheckWriting}
            placeholder="— select a check-writing account —"
          />
        </div>
        {bankAccounts.length === 0 && (
          <p className="field-error" style={{ fontSize: 12, marginBottom: 0 }}>
            No account is set up to write checks yet. Open{' '}
            <Link to="/accounts">Chart of Accounts</Link> and turn on{' '}
            <strong>Writes checks</strong> for the cash account these checks are drawn on.
          </p>
        )}
        {payees.length === 0 && (
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            No contacts yet — <Link to="/vendors">add a vendor</Link> to use as the payee.
          </p>
        )}

        {bankAccountId && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            {canQueue ? (
              <>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                  <input
                    type="radio"
                    checked={queueMode}
                    onChange={() => setQueueMode(true)}
                    disabled={isEdit && source?.status !== 'to_print'}
                  />{' '}
                  Queue for printing
                  <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                    number assigned when the batch prints
                  </span>
                </label>
                <label style={{ display: 'block', fontSize: 13 }}>
                  <input
                    type="radio"
                    checked={!queueMode}
                    onChange={() => setQueueMode(false)}
                    disabled={isEdit && source?.status === 'to_print'}
                  />{' '}
                  Record a check I wrote by hand
                  <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                    enter the number that is on the paper
                  </span>
                </label>
              </>
            ) : (
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                This account isn’t set up for check printing, so this records a check written by
                hand. <Link to="/settings/check-printing">Set it up</Link> to queue checks for the
                batch printer instead.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="check" style={{ marginBottom: 16 }}>
        <div className="check-band">
          <div>
            <div className="check-payer">{companyName}</div>
            {addressLines.map((l, i) => (
              <div key={i} className="muted" style={{ fontSize: 11 }}>{l}</div>
            ))}
          </div>
          <div className="check-no">
            <div>
              No.{' '}
              {isQueued ? (
                <span
                  className="muted"
                  style={{ fontStyle: 'italic', fontSize: 12 }}
                  title={`Will be around #${printSetup?.nextCheckNumber ?? ''} depending on what else is in the queue`}
                >
                  assigned at print
                </span>
              ) : (
                <input
                  value={checkNumber}
                  onChange={(e) => {
                    setNumberTouched(true);
                    setCheckNumber(e.target.value);
                  }}
                  style={{ width: 80, fontWeight: 700, textAlign: 'right' }}
                />
              )}
            </div>
            <div style={{ marginTop: 6 }}>
              Date <input type="date" value={checkDate} onChange={(e) => setCheckDate(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="check-row">
          <span className="check-label">Pay to the<br />order of</span>
          <ContactPicker
            companyId={companyId}
            value={payeeContactId}
            onChange={setPayeeContactId}
            defaultNewType="vendor"
            className="check-fill"
          />
          <span className="check-amount-box">{formatCurrency(totalStr)}</span>
        </div>
        {payeeAddrLines.length > 0 && (
          <div className="muted" style={{ fontSize: 11, marginLeft: 96, marginTop: 2 }}>
            {payeeAddrLines.join(' · ')}
          </div>
        )}

        <div className="check-words">
          <span className="check-fill">{total > 0 ? amountToWords(totalStr) : ''}</span>
          <span className="check-label">Dollars</span>
        </div>

        <div className="check-footer">
          <div style={{ flex: 1 }}>
            <span className="check-label">Memo</span>
            <input className="check-fill" style={{ marginTop: 2, width: '100%' }} value={memo} onChange={(e) => setMemo(e.target.value)} />
          </div>
          <div className="check-sign"><div className="check-sign-line">Authorized signature</div></div>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Expense allocation</h2>
        {payableCheques.length > 0 && (
          <div style={{ marginBottom: 10, fontSize: 13 }}>
            <label htmlFor="pay-cheque" style={{ marginRight: 8 }}>
              Pay an uncashed paycheque
            </label>
            <select
              id="pay-cheque"
              value=""
              onChange={(e) => {
                if (e.target.value) addPaycheque(e.target.value);
              }}
              title="Net pay the payroll run already booked to the holding account. Paying it here debits that account instead of an expense, so wages are not counted twice."
            >
              <option value="">— choose —</option>
              {payableCheques.map((c) => (
                <option key={c.journalLineId} value={c.journalLineId}>
                  {c.employeeName ?? c.entryNumber} · {formatAccountingDate(c.payDate)} ·{' '}
                  {formatCurrency(c.outstanding)}
                </option>
              ))}
            </select>
          </div>
        )}
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Account</th>
              <th>Memo</th>
              <th style={{ textAlign: 'right', width: 140 }}>Amount</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  {l.settlesJournalLineId ? (
                    <div style={{ fontSize: 13 }}>
                      {accounts.find((a) => a.id === l.accountId)?.code}{' '}
                      {accounts.find((a) => a.id === l.accountId)?.name}
                      <div className="muted" style={{ fontSize: 11 }}>{l.settlesLabel}</div>
                    </div>
                  ) : (
                    <AccountPicker
                      accounts={accounts}
                      value={l.accountId}
                      onChange={(id) => setLine(i, { accountId: id })}
                      predicate={isAllocSelectable}
                    />
                  )}
                </td>
                <td>
                  <input value={l.memo} onChange={(e) => setLine(i, { memo: e.target.value })} style={{ width: '100%' }} />
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    value={l.amount}
                    onChange={(e) => setLine(i, { amount: e.target.value })}
                    style={{ width: '100%', textAlign: 'right' }}
                    placeholder="0.00"
                  />
                </td>
                <td>
                  <button className="btn-danger" onClick={() => removeLine(i)} disabled={lines.length <= 1}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={2}>Check total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(totalStr)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        <button className="btn-secondary" onClick={addLine}>+ Add line</button>

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}
        {flash && <div className="tag ok" style={{ marginTop: 12, display: 'inline-block' }}>{flash}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn-primary" onClick={() => submit()} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save check'}
          </button>
          {!isEdit && (
            <button className="btn-secondary" onClick={() => submit(true)} disabled={!valid || mutation.isPending}>
              Save &amp; New
            </button>
          )}
          <Link
            to={isEdit ? '/checks/$id' : '/checks'}
            params={isEdit ? { id: editId! } : undefined}
            className="btn-secondary"
            style={{ textDecoration: 'none' }}
          >
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}
